import React, { useState, useEffect, useRef } from 'react';
import PrivacyPolicyContent from './PrivacyPolicyContent';

// Firebase
import { initializeApp } from 'firebase/app';
import {
  getAuth, onAuthStateChanged,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  GoogleAuthProvider, signInWithPopup,
  signOut, sendPasswordResetEmail,
} from 'firebase/auth';
import {
  getFirestore, collection, addDoc, doc, setDoc,
  query, where, onSnapshot, updateDoc,
} from 'firebase/firestore';

// Componenti
import CustomizationSection from './CustomizationSection';
import MenuSection from './MenuSection';
import TwoFlavorModal from './TwoFlavorModal';
import AuthModal from './AuthModal';

// Dati statici (fuori dal componente → non ricreati ad ogni render)
import {
  pizzas, specialtyPizzas, maxiPizzas, maxiSpecialtyPizzas,
  specialPizzas, friggitoriaItems, bevandeItems,
  availableAddableExtras as defaultExtras,
  removableIngredients as defaultRemovals,
  VARIANTS_DOC_ID,
} from './menuData';

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────
const roundUpToNearestHalf = (price) => Math.ceil(price * 2) / 2;

const emptyHalf = () => ({ selectedPizza: null, additions: [], removals: [], cost: 0 });

// ─────────────────────────────────────────────
//  Generatori slot orari
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
//  Logica giorni speciali
//  Ogni documento in closingDays può avere:
//    type: 'closing' | 'opening' | 'no_delivery'
//    session: 'full' | 'lunch' | 'dinner'
//    date, reason
// ─────────────────────────────────────────────
const getDayInfo = (dateString, specialDays) => {
  const dow = new Date(dateString).getDay();
  const isMonday = dow === 1;
  const isSunday = dow === 0;
  const forDay = specialDays.filter((d) => d.date === dateString);

  const extraOpen = forDay.find((d) => d.type === 'opening');

  // Compatibilità con vecchio formato (isFullDay: true, senza campo type)
  const closings = forDay.filter((d) =>
    d.type === 'closing' || (!d.type && d.isFullDay)
  );

  // isFullyClosed: lunedì senza extraOpen → tutto chiuso
  //                domenica senza extraOpen → NON tutto chiuso (cena aperta di default)
  //                altri giorni → solo se c'è una chiusura 'full'
  const isFullyClosed = isMonday
    ? !extraOpen
    : closings.some((d) => d.session === 'full' || (!d.session && d.isFullDay));

  let lunchClosed, dinnerClosed;

  if (isMonday) {
    // Lunedì: tutto chiuso di default, si apre solo con extraOpen
    const extraOpenLunch  = extraOpen && (extraOpen.session === 'full' || extraOpen.session === 'lunch');
    const extraOpenDinner = extraOpen && (extraOpen.session === 'full' || extraOpen.session === 'dinner');
    lunchClosed  = !extraOpenLunch;
    dinnerClosed = !extraOpenDinner;
  } else if (isSunday) {
    // Domenica: pranzo chiuso di default, cena aperta di default
    // extraOpen può aprire il pranzo, closings può chiudere la cena
    const sundayLunchOpen = extraOpen && (extraOpen.session === 'full' || extraOpen.session === 'lunch');
    lunchClosed  = !sundayLunchOpen;
    dinnerClosed = isFullyClosed || closings.some((d) => d.session === 'dinner');
  } else {
    // Tutti gli altri giorni: tutto aperto, si chiude solo con closings
    lunchClosed  = isFullyClosed || closings.some((d) => d.session === 'lunch');
    dinnerClosed = isFullyClosed || closings.some((d) => d.session === 'dinner');
  }

  const noDeliveries = forDay.filter((d) => d.type === 'no_delivery');
  const deliveryFullyDisabled  = noDeliveries.some((d) => d.session === 'full');
  const deliveryLunchDisabled  = deliveryFullyDisabled || noDeliveries.some((d) => d.session === 'lunch');
  const deliveryDinnerDisabled = deliveryFullyDisabled || noDeliveries.some((d) => d.session === 'dinner');

  return {
    isMonday, isSunday, isFullyClosed,
    lunchClosed,
    dinnerClosed,
    deliveryLunchDisabled,
    deliveryDinnerDisabled,
    extraOpen, closings, noDeliveries,
  };
};

const generateDeliveryTimeSlots = (dateString, specialDays) => {
  const slots = [];
  const today = new Date(); today.setHours(0,0,0,0);
  const sel = new Date(dateString); sel.setHours(0,0,0,0);
  const now = new Date();
  const isToday = sel.getTime() === today.getTime();
  const info = getDayInfo(dateString, specialDays);
  if (info.isFullyClosed) return [];
  const addSlot = (h, m) => {
    const s = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate(), h, m);
    if (isToday && s < now) return;
    const nh = m+30>=60 ? h+1 : h; const nm = (m+30)%60;
    slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}-${String(nh).padStart(2,'0')}:${String(nm).padStart(2,'0')}`);
  };
  if (!info.lunchClosed && !info.deliveryLunchDisabled) {
    for (let h=11;h<=13;h++) for (let m=h===11?30:0;m<60;m+=30) { if(h===13&&m>0)break; addSlot(h,m); }
    if (slots.length) slots.push('---');
  }
  if (!info.dinnerClosed && !info.deliveryDinnerDisabled) {
    for (let h=18;h<=21;h++) for (let m=h===18?30:0;m<60;m+=30) { if(h===21&&m>0)break; addSlot(h,m); }
  }
  if (slots[slots.length-1]==='---') slots.pop();
  return slots;
};

const generatePickupTimeSlots = (dateString, specialDays) => {
  const slots = [];
  const today = new Date(); today.setHours(0,0,0,0);
  const sel = new Date(dateString); sel.setHours(0,0,0,0);
  const now = new Date();
  const isToday = sel.getTime() === today.getTime();
  const info = getDayInfo(dateString, specialDays);
  if (info.isFullyClosed) return [];
  const addSlot = (h, m) => {
    const s = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate(), h, m);
    if (isToday && s < now) return;
    slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
  };
  if (!info.lunchClosed) {
    for (let h=12;h<=13;h++) for (let m=0;m<60;m+=10) { if(h===13&&m>50)break; addSlot(h,m); }
    if (slots.length) slots.push('---');
  }
  if (!info.dinnerClosed) {
    for (let h=18;h<=22;h++) for (let m=0;m<60;m+=10) { if(h===22&&m>0)break; addSlot(h,m); }
  }
  if (slots[slots.length-1]==='---') slots.pop();
  return slots;
};


// ─────────────────────────────────────────────
//  App
// ─────────────────────────────────────────────
const App = () => {
  const appId = typeof __app_id !== 'undefined' ? __app_id : '1:570970254118:web:04584a5a3e82ebe5e12fe7';

  // ── UI state ──
  const [activeMenuTab, setActiveMenuTab] = useState('normalPizzas');
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // ── Cart ──
  const [cart, setCart] = useState([]);

  // ── Order form ──
  const [deliveryOption, setDeliveryOption] = useState('pickup');
  const [address, setAddress] = useState({ street: '', city: '', zip: '', phone: '', intercomName: '' });
  const [pickupName, setPickupName] = useState('');
  const [pickupPhone, setPickupPhone] = useState('');  // FIX: corretto nome variabile
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [orderNotes, setOrderNotes] = useState('');

  // ── Auth ──
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);

  // ── Orders history ──
  const [notifiedOrderIds, setNotifiedOrderIds] = useState(new Set());
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [userOrders, setUserOrders] = useState([]);
  const [cancelOrderModal, setCancelOrderModal] = useState(null);

  // ── Two-flavor normal ──
  const [showTwoFlavorModal, setShowTwoFlavorModal] = useState(false);
  const [twoFlavorStep, setTwoFlavorStep] = useState(1);
  const [firstHalfDetails, setFirstHalfDetails] = useState(emptyHalf());
  const [secondHalfDetails, setSecondHalfDetails] = useState(emptyHalf());

  // ── Two-flavor maxi ──
  const [showMaxiTwoFlavorModal, setShowMaxiTwoFlavorModal] = useState(false);
  const [maxiTwoFlavorStep, setMaxiTwoFlavorStep] = useState(1);
  const [firstHalfMaxiDetails, setFirstHalfMaxiDetails] = useState(emptyHalf());
  const [secondHalfMaxiDetails, setSecondHalfMaxiDetails] = useState(emptyHalf());

  // ── Customization single pizza ──
  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [itemToCustomize, setItemToCustomize] = useState(null);
  const [customAdditions, setCustomAdditions] = useState([]);
  const [customRemovals, setCustomRemovals] = useState([]);
  const [customCost, setCustomCost] = useState(0);

  // Search state is now internal to CustomizationSection and TwoFlavorModal

  // ── Firebase ──
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);

  // ── Menu availability ──
  // Menu items from Firestore (source of truth)
  const [menuItemsLoading, setMenuItemsLoading] = useState(true);
  const [fsMenuItems, setFsMenuItems] = useState([]);        // all products
  const [fsExtras, setFsExtras] = useState(defaultExtras);   // addable extras
  const [fsRemovals, setFsRemovals] = useState(defaultRemovals); // removable ingredients

  // Derived menu arrays - fall back to static data if Firestore not yet loaded
  // All pizzas sorted alphabetically (Firestore non garantisce l'ordine)
  const alpha = (a, b) => a.name.localeCompare(b.name, 'it');
  const menuPizzas             = fsMenuItems.filter(i => i.type === 'pizza' && !i.id.startsWith('s')).sort(alpha);
  const menuSpecialtyPizzas    = fsMenuItems.filter(i => i.type === 'pizza' && i.id.startsWith('s')).sort(alpha);
  const menuMaxiPizzas         = fsMenuItems.filter(i => i.type === 'maxi-pizza' && !i.id.startsWith('maxi-s')).sort(alpha);
  const menuMaxiSpecialtyPizzas= fsMenuItems.filter(i => i.type === 'maxi-pizza' && i.id.startsWith('maxi-s')).sort(alpha);
  const menuSpecialPizzas      = fsMenuItems.filter(i => i.type === 'special-pizza');
  const menuFriggitoria        = fsMenuItems.filter(i => i.type === 'friggitoria').sort(alpha);
  const menuBevande            = fsMenuItems.filter(i => i.type === 'bevanda').sort((a, b) => {
    if (a.category !== b.category) return a.category === 'non-alcoholic' ? -1 : 1;
    return alpha(a, b);
  });

  // ── Closing days (read-only, managed from StaffApp) ──
  const [closingDaysList, setClosingDaysList] = useState([]);

  // ── Cookies ──
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({ functional: true, analytics: false, marketing: false });
  const [showCookieSettingsModal, setShowCookieSettingsModal] = useState(false);

  const cartRef = useRef(null);
  const privacyPolicyRef = useRef(null);

  // ─────────────────────────────────────────────
  //  Firebase init
  // ─────────────────────────────────────────────
  useEffect(() => {
    const initFirebase = async () => {
      try {
        // SICUREZZA: usa la variabile d'ambiente __firebase_config se disponibile.
        // Non esporre mai le chiavi in produzione senza variabili d'ambiente.
        const firebaseConfig = JSON.parse(
          typeof __firebase_config !== 'undefined'
            ? __firebase_config
            : JSON.stringify({
                apiKey: "AIzaSyBQxITYm7nZavHKQ74WVE-E2E6pwadhwrM",
                authDomain: "app-ordini-pizzeria-cavour.firebaseapp.com",
                projectId: "app-ordini-pizzeria-cavour",
                storageBucket: "app-ordini-pizzeria-cavour.firebasestorage.app",
                messagingSenderId: "570970254118",
                appId: "1:570970254118:web:04584a5a3e82ebe5e12fe7",
              })
        );
        const app = initializeApp(firebaseConfig);
        const firestore = getFirestore(app);
        const authInstance = getAuth(app);
        setDb(firestore);
        setAuth(authInstance);
        setIsFirebaseReady(true);

        onAuthStateChanged(authInstance, async (user) => {
          if (user) {
            setCurrentUser(user);
            try {
              await setDoc(doc(firestore, 'users', user.uid), {
                email: user.email || null,
                displayName: user.displayName || null,
                lastLogin: new Date().toISOString(),
              }, { merge: true });
            } catch (e) {
              console.error('Firestore user save error:', e);
            }
          } else {
            setCurrentUser(null);
          }
        });
      } catch (error) {
        console.error('Firebase init error:', error);
        setMessage('Errore di sistema: impossibile inizializzare i servizi di ordine. Riprova più tardi.');
        setShowModal(true);
      }
    };
    initFirebase();
  }, []);

  // ── Notifiche ordini real-time ──
  useEffect(() => {
    if (!db || !currentUser?.uid || !isFirebaseReady) return;
    const q = query(collection(db, `artifacts/${appId}/public/data/orders`), where('userId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const order = change.doc.data();
        const { orderId } = order;
        const prevStatus = change.oldDoc?.data()?.status;
        if (change.type !== 'modified' || notifiedOrderIds.has(orderId)) return;

        let msg = '';
        if (order.status === 'approved' && prevStatus !== 'approved') {
          msg = `Il tuo ordine ${orderId} è stato confermato.`;
          const time = order.confirmedTime || order.selectedTime;
          if (time) msg += ` Orario di ${order.deliveryOption}: ${time}.`;
          if (order.selectedTime && order.confirmedTime && order.selectedTime !== order.confirmedTime)
            msg += ` (Orario richiesto era ${order.selectedTime}, modificato a ${order.confirmedTime})`;
          if (order.staffMessage) msg += ` Messaggio staff: ${order.staffMessage}`;
        } else if (order.status === 'rejected' && prevStatus !== 'rejected') {
          msg = `Il tuo ordine ${orderId} è stato annullato.`;
          if (order.staffMessage) msg += ` Messaggio staff: ${order.staffMessage}`;
        }
        if (msg) {
          setMessage(msg);
          setShowModal(true);
          setNotifiedOrderIds((prev) => new Set(prev).add(orderId));
        }
      });
    });
    return () => unsub();
  }, [db, currentUser, appId, notifiedOrderIds, isFirebaseReady]);

  // ── Storico ordini ──
  useEffect(() => {
    if (!db || !currentUser?.uid || !showOrderHistory || !isFirebaseReady) { setUserOrders([]); return; }
    const q = query(collection(db, `artifacts/${appId}/public/data/orders`), where('userId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      orders.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setUserOrders(orders);
    });
    return () => unsub();
  }, [db, currentUser, appId, showOrderHistory, isFirebaseReady]);

  // ── Disponibilità menu ──
  useEffect(() => {
    if (!db || !isFirebaseReady) return;
    const unsub = onSnapshot(
      collection(db, `artifacts/${appId}/public/data/menuItems`),
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({ id: d.id, docId: d.id, ...d.data() }));
        setFsMenuItems(items);
        setMenuItemsLoading(false);
      }
    );
    return () => unsub();
  }, [db, appId, isFirebaseReady]);

  // ── Varianti (extras/removals) da Firestore ──
  useEffect(() => {
    if (!db || !isFirebaseReady) return;
    const variantsRef = doc(db, `artifacts/${appId}/public/data/menuConfig`, VARIANTS_DOC_ID);
    // use onSnapshot so changes reflect immediately
    const unsub = onSnapshot(variantsRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.extras)   setFsExtras(data.extras);
        if (data.removals) setFsRemovals(data.removals);
      }
    });
    return () => unsub();
  }, [db, appId, isFirebaseReady]);

  // ── Giorni di chiusura ──
  useEffect(() => {
    if (!db || !isFirebaseReady) return;
    const unsub = onSnapshot(
      collection(db, `artifacts/${appId}/public/data/closingDays`),
      (snapshot) => setClosingDaysList(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, [db, appId, isFirebaseReady]);

  // ── Cookie consent ──
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      const prefs = JSON.parse(localStorage.getItem('cookiePreferences') || '{}');
      setCookiePreferences({ functional: true, analytics: prefs.analytics || false, marketing: prefs.marketing || false });
      setShowCookieConsent(false);
    } else {
      setShowCookieConsent(true);
    }
  }, []);

  // ─────────────────────────────────────────────
  //  Helpers
  // ─────────────────────────────────────────────
  const isItemAvailable = (itemId) => {
    if (menuItemsLoading) return true;
    const found = fsMenuItems.find((m) => m.id === itemId);
    return found ? found.isAvailable : false;
  };

  const isUserAuthenticated = !!currentUser;

  const currentTimeslots = deliveryOption === 'delivery'
    ? generateDeliveryTimeSlots(selectedDate, closingDaysList)
    : generatePickupTimeSlots(selectedDate, closingDaysList);

  // ── Avvisi giorno selezionato ──
  const dayInfo = getDayInfo(selectedDate, closingDaysList);

  // Messaggio principale (chiusura/apertura)
  const dayStatusMessage = (() => {
    if (dayInfo.isMonday && !dayInfo.extraOpen)
      return { color: 'red', text: '⛔ Siamo chiusi il lunedì.' };
    if (dayInfo.extraOpen) {
      const sessionLabel = dayInfo.extraOpen.session === 'full' ? 'tutto il giorno'
        : dayInfo.extraOpen.session === 'lunch' ? 'solo a pranzo' : 'solo a cena';
      return { color: 'green', text: `✅ Apertura straordinaria (${sessionLabel})${dayInfo.extraOpen.reason ? ': ' + dayInfo.extraOpen.reason : ''}` };
    }
    const fullClose = dayInfo.closings.find((d) => d.session === 'full');
    if (fullClose) return { color: 'red', text: `⛔ Chiusura straordinaria: ${fullClose.reason || ''}` };
    const lunchClose = dayInfo.closings.find((d) => d.session === 'lunch');
    const dinnerClose = dayInfo.closings.find((d) => d.session === 'dinner');
    if (lunchClose && dinnerClose) return { color: 'red', text: `⛔ Chiusi sia a pranzo che a cena.` };
    if (lunchClose) return { color: 'orange', text: `⚠️ Chiusi a pranzo${lunchClose.reason ? ': ' + lunchClose.reason : ''}. Disponibili solo gli orari serali.` };
    if (dinnerClose) return { color: 'orange', text: `⚠️ Chiusi a cena${dinnerClose.reason ? ': ' + dinnerClose.reason : ''}. Disponibili solo gli orari di pranzo.` };
    return null;
  })();

  // Avviso consegna disabilitata
  const deliveryNotice = (() => {
    if (deliveryOption !== 'delivery') return null;
    const nd = dayInfo.noDeliveries;
    const fullNd = nd.find((d) => d.session === 'full');
    if (fullNd) return `🚫 Consegna a domicilio non disponibile oggi${fullNd.reason ? ': ' + fullNd.reason : ''}.`;
    const lunchNd = nd.find((d) => d.session === 'lunch');
    const dinnerNd = nd.find((d) => d.session === 'dinner');
    if (lunchNd && dinnerNd) return '🚫 Consegna a domicilio non disponibile oggi.';
    if (lunchNd) return `🚫 Consegna non disponibile a pranzo${lunchNd.reason ? ': ' + lunchNd.reason : ''}.`;
    if (dinnerNd) return `🚫 Consegna non disponibile a cena${dinnerNd.reason ? ': ' + dinnerNd.reason : ''}.`;
    return null;
  })();

  // ─────────────────────────────────────────────
  //  Cart
  // ─────────────────────────────────────────────
  const triggerAddFeedback = (itemId) => {
    setLoadingItemId(itemId);
    setTimeout(() => {
      setLoadingItemId(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 1200);
    }, 500);
  };

  const addItemToCart = (item, additions = [], removals = []) => {
    triggerAddFeedback(item.id);
    setCart((prev) => {
      const hasMods = additions.length > 0 || removals.length > 0;
      const displayMods = [
        ...additions.map((m) => `+${m.name} (€${m.price.toFixed(2)})`),
        ...removals.map((m) => `-${m}`),
      ];
      const finalPrice = parseFloat((item.price + additions.reduce((s, m) => s + m.price, 0)).toFixed(2));

      if (!hasMods && !(item.firstHalfMods || item.secondHalfMods)) {
        // Prova a raggruppare con un elemento identico già nel carrello
        const idx = prev.findIndex(
          (c) => c.id === item.id && c.type === item.type &&
            (!c.modifications || c.modifications.length === 0) &&
            (!c.firstHalfMods || c.firstHalfMods.length === 0)
        );
        if (idx > -1) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
          return updated;
        }
      }
      return [
        ...prev,
        {
          ...item,
          price: finalPrice,
          modifications: displayMods,
          rawAdditions: additions,
          rawRemovals: removals,
          cartItemId: `${item.id}-${Date.now()}`,
          quantity: 1,
        },
      ];
    });
  };

  const addItemToCartDirectly = (item) => {
    triggerAddFeedback(item.id);
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.id === item.id && c.type === item.type && (!c.modifications || c.modifications.length === 0));
      if (idx > -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
        return updated;
      }
      return [...prev, { ...item, modifications: [], cartItemId: `${item.id}-${Date.now()}`, quantity: 1 }];
    });
  };

  const decreaseQuantity = (cartItemId) =>
    setCart((prev) => prev.map((i) => i.cartItemId === cartItemId ? { ...i, quantity: i.quantity - 1 } : i).filter((i) => i.quantity > 0));

  const increaseQuantity = (cartItemId) =>
    setCart((prev) => prev.map((i) => i.cartItemId === cartItemId ? { ...i, quantity: i.quantity + 1 } : i));

  const removeFromCart = (cartItemId) =>
    setCart((prev) => prev.filter((i) => i.cartItemId !== cartItemId));

  const clearCart = () => { setCart([]); setMessage('Il carrello è stato svuotato.'); setShowModal(true); };

  const cartTotalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  // Calcola costo consegna in base al subtotale
  // < 10€   → 4.50€ (mostrato come 3.50€ + 1€ supplemento piccolo ordine)
  // < 30€   → 3.50€
  // < 70€   → 2.50€
  // < 100€  → 2.00€
  // >= 100€ → gratis
  const getDeliveryFee = (subtotal) => {
    if (subtotal >= 100) return 0;
    if (subtotal >= 70)  return 2.00;
    if (subtotal >= 30)  return 2.50;
    if (subtotal >= 10)  return 3.50;
    return 4.50;
  };

  const deliveryFee = deliveryOption === 'delivery' && cart.length > 0
    ? getDeliveryFee(cartSubtotal)
    : 0;

  const calculateTotal = () => {
    return (cartSubtotal + deliveryFee).toFixed(2);
  };

  // ─────────────────────────────────────────────
  //  Due Gusti
  // ─────────────────────────────────────────────
  const openTwoFlavorModal = (isMaxi) => {
    if (isMaxi) {
      setFirstHalfMaxiDetails(emptyHalf()); setSecondHalfMaxiDetails(emptyHalf());
      setMaxiTwoFlavorStep(1); setShowMaxiTwoFlavorModal(true);
    } else {
      setFirstHalfDetails(emptyHalf()); setSecondHalfDetails(emptyHalf());
      setTwoFlavorStep(1); setShowTwoFlavorModal(true);
    }
    // search state is internal to TwoFlavorModal/CustomizationSection
  };

  const finalizeTwoFlavorPizza = (isMaxi) => {
    const first = isMaxi ? firstHalfMaxiDetails : firstHalfDetails;
    const second = isMaxi ? secondHalfMaxiDetails : secondHalfDetails;
    if (!first.selectedPizza || !second.selectedPizza) {
      setMessage('Seleziona entrambe le metà per creare una pizza due gusti.');
      setShowModal(true); return;
    }
    if (!isItemAvailable(first.selectedPizza.id) || !isItemAvailable(second.selectedPizza.id)) {
      setMessage('Impossibile creare una pizza due gusti con un gusto non disponibile.');
      setShowModal(true); return;
    }
    const rawTotal = (first.selectedPizza.price + first.cost) + (second.selectedPizza.price + second.cost);
    const finalPrice = roundUpToNearestHalf(rawTotal / 2);

    const twoFlavorItem = {
      id: `two-flavor-${first.selectedPizza.id}-${second.selectedPizza.id}-${isMaxi ? 'maxi' : 'normal'}-${Date.now()}`,
      name: `Pizza ${isMaxi ? 'Maxi ' : ''}Due Gusti: ${first.selectedPizza.name} / ${second.selectedPizza.name}`,
      description: '',
      price: parseFloat(finalPrice.toFixed(2)),
      type: isMaxi ? 'maxi-pizza-due-gusti' : 'pizza-due-gusti',
      isMaxi,
      firstHalfMods: [
        ...first.additions.map((m) => `+${m.name} (€${m.price.toFixed(2)})`),
        ...first.removals.map((m) => `-${m}`),
      ],
      secondHalfMods: [
        ...second.additions.map((m) => `+${m.name} (€${m.price.toFixed(2)})`),
        ...second.removals.map((m) => `-${m}`),
      ],
      modifications: [],
      rawFirstHalf: first,
      rawSecondHalf: second,
    };
    addItemToCart(twoFlavorItem);
    if (isMaxi) {
      setShowMaxiTwoFlavorModal(false);
      setFirstHalfMaxiDetails(emptyHalf()); setSecondHalfMaxiDetails(emptyHalf());
      setMaxiTwoFlavorStep(1);
    } else {
      setShowTwoFlavorModal(false);
      setFirstHalfDetails(emptyHalf()); setSecondHalfDetails(emptyHalf());
      setTwoFlavorStep(1);
    }
  };

  // ─────────────────────────────────────────────
  //  Customization (pizza singola)
  // ─────────────────────────────────────────────
  const openCustomizationModal = (item) => {
    setItemToCustomize(item);
    setCustomAdditions([]); setCustomRemovals([]); setCustomCost(0);
    setShowCustomizationModal(true);
  };

  const closeCustomizationModal = () => {
    setShowCustomizationModal(false); setItemToCustomize(null);
    setCustomAdditions([]); setCustomRemovals([]); setCustomCost(0);
  };

  // ─────────────────────────────────────────────
  //  Ordine
  // ─────────────────────────────────────────────
  const placeOrder = async () => {
    if (cart.length === 0) { setMessage('Il carrello è vuoto.'); setShowModal(true); return; }
    if (!selectedTime || !selectedDate) { setMessage('Seleziona data e orario.'); setShowModal(true); return; }

    // Usa getDayInfo per la validazione — rispetta aperture/chiusure straordinarie
    const info = getDayInfo(selectedDate, closingDaysList);

    if (info.isFullyClosed) {
      const fullClose = info.closings.find(d => d.session === 'full' || (!d.session && d.isFullDay));
      const reason = fullClose?.reason || (info.isMonday ? 'Siamo chiusi il lunedì.' : 'Chiusura straordinaria.');
      setMessage(reason);
      setShowModal(true);
      return;
    }

    // Controlla che l'orario selezionato sia in una fascia aperta
    const selHour = parseInt(selectedTime.split(':')[0]);
    const isLunchHour = selHour >= 11 && selHour < 14;
    const isDinnerHour = selHour >= 18 && selHour <= 22;

    if (isLunchHour && info.lunchClosed) {
      setMessage('Il negozio è chiuso a pranzo in questa data.');
      setShowModal(true);
      return;
    }
    if (isDinnerHour && info.dinnerClosed) {
      setMessage('Il negozio è chiuso a cena in questa data.');
      setShowModal(true);
      return;
    }
    if (deliveryOption === 'delivery' && isLunchHour && info.deliveryLunchDisabled) {
      setMessage('La consegna a domicilio non è disponibile a pranzo in questa data.');
      setShowModal(true);
      return;
    }
    if (deliveryOption === 'delivery' && isDinnerHour && info.deliveryDinnerDisabled) {
      setMessage('La consegna a domicilio non è disponibile a cena in questa data.');
      setShowModal(true);
      return;
    }
    if (!currentUser) { setMessage('Accedi o registrati per inviare l\'ordine.'); setShowModal(true); return; }
    if (!isFirebaseReady || !db) { setMessage('Sistema non pronto. Riprova.'); setShowModal(true); return; }

    const orderId = 'ORD' + Math.floor(100000 + Math.random() * 900000);
    const orderDetails = {
      cart, total: parseFloat(calculateTotal()),
      deliveryOption, selectedDate, selectedTime,
      timestamp: new Date().toISOString(),
      status: 'pending', orderNotes,
      confirmedTime: null, staffMessage: null,
      orderId, userId: currentUser.uid,
    };

    if (deliveryOption === 'pickup') {
      // FIX: controlla la quantità totale (non il numero di righe)
      if (!pickupName.trim()) { setMessage('Inserisci il nome per il ritiro.'); setShowModal(true); return; }
      if (!pickupPhone.trim()) { setMessage('Inserisci il numero di telefono per il ritiro.'); setShowModal(true); return; }
      orderDetails.pickupName = pickupName.trim();
      orderDetails.pickupPhone = pickupPhone.trim();
    }

    if (deliveryOption === 'delivery') {
      if (!address.street || !address.city || !address.zip || !address.phone || !address.intercomName) {
        setMessage('Compila tutti i campi dell\'indirizzo.'); setShowModal(true); return;
      }
      orderDetails.address = address;
    }

    try {
      await addDoc(collection(db, `artifacts/${appId}/public/data/orders`), orderDetails);
      let confirmMsg = `Ordine inviato con successo! Il tuo numero ordine è ${orderId}. Riceverai una conferma via email. Grazie!`;
      if (deliveryOption === 'pickup') confirmMsg += ` Nome ritiro: ${pickupName}.`;
      if (orderNotes) confirmMsg += ` Note: ${orderNotes}.`;
      setMessage(confirmMsg); setShowModal(true);

      // Reset form
      setCart([]);
      setAddress({ street: '', city: '', zip: '', phone: '', intercomName: '' });
      setPickupName(''); setPickupPhone('');
      setSelectedTime('');
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setOrderNotes('');
    } catch (error) {
      console.error('Errore ordine:', error);
      setMessage('Errore durante l\'invio dell\'ordine: ' + error.message);
      setShowModal(true);
    }
  };

  // ─────────────────────────────────────────────
  //  Annullamento ordine
  // ─────────────────────────────────────────────
  // FIX: parsing corretto dell'orario
  const confirmCancelOrder = (docId, selectedTime, confirmedTime, deliveryOption, orderDate) => {
    const targetTimeStr = confirmedTime || selectedTime;
    if (!targetTimeStr || !orderDate) {
      setMessage("Impossibile determinare data/orario dell'ordine."); setShowModal(true); return;
    }
    // FIX: parse corretto – prende solo la prima parte (es. "19:30" da "19:30-20:00")
    const timePart = targetTimeStr.split('-')[0];
    const [hours, minutes] = timePart.split(':').map(Number);
    const targetDate = new Date(orderDate);
    targetDate.setHours(hours, minutes, 0, 0);

    const now = new Date();
    if (targetDate < now) { setMessage("Questo ordine è già passato. Non è più possibile annullarlo."); setShowModal(true); return; }

    const diffMinutes = Math.floor((targetDate - now) / 60000);
    if (diffMinutes > 30) {
      setCancelOrderModal({ orderId: docId, docId, orderTime: targetTimeStr, orderDate, isDelivery: deliveryOption === 'delivery', timeRemaining: diffMinutes });
    } else {
      setMessage("Puoi annullare un ordine solo se mancano più di 30 minuti all'orario previsto."); setShowModal(true);
    }
  };

  const handleCancelOrder = async () => {
    if (!db || !cancelOrderModal) return;
    try {
      await updateDoc(doc(db, `artifacts/${appId}/public/data/orders`, cancelOrderModal.docId), {
        status: 'cancelled', staffMessage: 'Ordine annullato dal cliente.',
      });
      setMessage(`L'ordine ${cancelOrderModal.orderId} è stato annullato.`);
      setShowModal(true); setCancelOrderModal(null);
    } catch (error) {
      setMessage('Errore nell\'annullamento: ' + error.message);
      setShowModal(true); setCancelOrderModal(null);
    }
  };

  // ─────────────────────────────────────────────
  //  Auth
  // ─────────────────────────────────────────────
  const handleAuthSubmit = async (e) => {
    e.preventDefault(); setAuthError('');
    if (!auth || !authEmail || (authMode !== 'reset_password' && !authPassword)) { setAuthError('Compila tutti i campi.'); return; }
    if (authMode === 'register' && !privacyPolicyAccepted) { setAuthError('Devi accettare la Privacy Policy.'); return; }

    try {
      if (authMode === 'register') {
        const cred = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        await setDoc(doc(db, 'users', cred.user.uid), { email: cred.user.email, createdAt: new Date().toISOString() });
      } else if (authMode === 'login') {
        const cred = await signInWithEmailAndPassword(auth, authEmail, authPassword);
        await setDoc(doc(db, 'users', cred.user.uid), { email: cred.user.email, lastLogin: new Date().toISOString() }, { merge: true });
      } else if (authMode === 'reset_password') {
        await sendPasswordResetEmail(auth, authEmail);
        setAuthError('Email per il reset inviata! Controlla la casella di posta.'); setAuthEmail(''); return;
      }
      setShowAuthModal(false);
    } catch (error) {
      const msgs = {
        'auth/email-already-in-use': 'Email già registrata.',
        'auth/invalid-email': 'Formato email non valido.',
        'auth/weak-password': 'Password troppo corta (min 6 caratteri).',
        'auth/user-not-found': 'Email o password non valide.',
        'auth/wrong-password': 'Email o password non valide.',
        'auth/too-many-requests': 'Troppi tentativi. Riprova più tardi.',
        'auth/account-exists-with-different-credential': 'Account già esistente con email diversa. Prova il login con email/password.',
      };
      setAuthError(msgs[error.code] || `Errore: ${error.message}`);
    }
  };

  const handleSocialLogin = async (providerName) => {
    setAuthError('');
    if (!auth) { setAuthError('Servizio non pronto.'); return; }
    if (authMode === 'register' && !privacyPolicyAccepted) { setAuthError('Devi accettare la Privacy Policy.'); return; }
    const provider = providerName === 'google' ? new GoogleAuthProvider() : null;
    if (!provider) { setAuthError('Provider non supportato.'); return; }
    try {
      const cred = await signInWithPopup(auth, provider);
      await setDoc(doc(db, 'users', cred.user.uid), {
        email: cred.user.email, displayName: cred.user.displayName,
        photoURL: cred.user.photoURL, lastLogin: new Date().toISOString(),
      }, { merge: true });
      setShowAuthModal(false);
    } catch (error) {
      const msgs = {
        'auth/account-exists-with-different-credential': 'Esiste già un account con questa email.',
        'auth/popup-closed-by-user': 'Autenticazione annullata.',
      };
      setAuthError(msgs[error.code] || `Errore login ${providerName}.`);
    }
  };

  const handleLogout = async () => {
    try { await signOut(auth); }
    catch (e) { setMessage('Errore durante il logout.'); setShowModal(true); }
  };

  // ─────────────────────────────────────────────
  //  Cookie
  // ─────────────────────────────────────────────
  const handleAcceptAllCookies = () => {
    const prefs = { functional: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', 'accepted'); localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    setCookiePreferences(prefs); setShowCookieConsent(false); setShowCookieSettingsModal(false);
  };
  const handleRejectAllCookies = () => {
    const prefs = { functional: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', 'rejected'); localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    setCookiePreferences(prefs); setShowCookieConsent(false); setShowCookieSettingsModal(false);
  };
  const handleSaveCookiePreferences = () => {
    localStorage.setItem('cookieConsent', 'custom'); localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowCookieConsent(false); setShowCookieSettingsModal(false);
  };

  const scrollToCart = () => cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const getMinDate = () => new Date().toISOString().split('T')[0];

  // ─────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 font-sans text-gray-800 flex flex-col items-center p-4 sm:p-6 md:p-8">

      {/* ── Header ── */}
      <header className="w-full max-w-4xl bg-red-700 text-white p-6 rounded-lg shadow-lg mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full">
          <div className="text-center sm:text-left mb-4 sm:mb-0 flex-grow">
            <img
              src="/logo_CAVOUR.jpg"
              alt="Logo Pizzeria Cavour"
              onError={(e) => { e.target.src = 'https://placehold.co/250x80/FF0000/FFFFFF?text=Pizzeria+Cavour'; }}
              className="mx-auto w-full h-auto object-contain rounded-md"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-center sm:justify-end">
            {!isUserAuthenticated && (
              <button onClick={() => { setShowAuthModal(true); setAuthMode('login'); setAuthError(''); setAuthEmail(''); setAuthPassword(''); setPrivacyPolicyAccepted(false); }}
                className="bg-red-500 text-white px-3 py-1 rounded-full shadow-md hover:bg-red-600 transition-colors text-sm">
                Accedi / Registrati
              </button>
            )}
            {isUserAuthenticated && (
              <span className="text-gray-100 text-sm hidden sm:block">
                Benvenuto, {currentUser?.displayName || currentUser?.email?.split('@')[0]}!
              </span>
            )}
            {isUserAuthenticated && (
              <>
                <button onClick={() => setShowOrderHistory(true)} className="bg-yellow-500 text-white px-3 py-1 rounded-full shadow-md hover:bg-yellow-600 transition-colors text-sm">I Miei Ordini</button>
                <button onClick={handleLogout} className="bg-white text-red-600 px-3 py-1 rounded-full shadow-md hover:bg-gray-100 transition-colors text-sm">Logout</button>

              </>
            )}
            <button onClick={scrollToCart} className="bg-white text-red-600 px-4 py-2 rounded-full shadow-md hover:bg-gray-100 transition-colors flex items-center text-sm" aria-label="Vai al Carrello">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                <circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" />
                <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
              </svg>
              Carrello ({cartTotalItems})
            </button>
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8">

        {/* Tab buttons */}
        <div className="md:col-span-2 flex flex-wrap justify-center gap-3 mb-2">
          {[
            { key: 'normalPizzas', label: 'Pizze Normali' },
            { key: 'maxiPizzas',   label: 'Pizze Maxi' },
            { key: 'specialPizzas',label: '✨ Pizza della settimana ✨' },
            { key: 'friggitoria',  label: 'La Friggitoria' },
            { key: 'bevande',      label: 'Bevande' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setActiveMenuTab(key)}
              className={`px-5 py-2 rounded-lg shadow-md font-bold transition-colors duration-200 ${activeMenuTab === key ? 'bg-red-600 text-white' : 'bg-white text-red-600 hover:bg-red-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Pizze normali */}
        {activeMenuTab === 'normalPizzas' && (
          <section className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
            <h2 className="text-2xl font-bold mb-4 text-red-700">Le Nostre Pizze Normali</h2>
            <button onClick={() => openTwoFlavorModal(false)}
              className="mb-4 w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors text-lg">
              Crea Pizza Due Gusti 🍕🍕
            </button>
            <MenuSection
              title=""
              items={menuItemsLoading ? [...pizzas, ...specialtyPizzas] : [...menuPizzas, ...menuSpecialtyPizzas]}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onCustomize={openCustomizationModal}
              onAddToCart={(item) => addItemToCart(item)}
              showSearch={true}
            />
          </section>
        )}

        {/* Pizze maxi */}
        {activeMenuTab === 'maxiPizzas' && (
          <section className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
            <h2 className="text-2xl font-bold mb-4 text-red-700">Le Nostre Pizze Maxi</h2>
            <button onClick={() => openTwoFlavorModal(true)}
              className="mb-4 w-full bg-red-800 hover:bg-red-900 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors text-lg">
              Crea Pizza Maxi Due Gusti 🍕🍕
            </button>
            <MenuSection
              title=""
              items={menuItemsLoading ? [...maxiPizzas, ...maxiSpecialtyPizzas] : [...menuMaxiPizzas, ...menuMaxiSpecialtyPizzas]}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onCustomize={openCustomizationModal}
              onAddToCart={(item) => addItemToCart(item)}
              showSearch={true}
            />
          </section>
        )}

        {/* Pizze speciali */}
        {activeMenuTab === 'specialPizzas' && (
          <MenuSection
            title="La nostra proposta settimanale ✨🍕"
            items={menuItemsLoading ? specialPizzas : menuSpecialPizzas}
            isItemAvailable={isItemAvailable}
            loadingItemId={loadingItemId}
            onCustomize={openCustomizationModal}
            onAddToCart={(item) => addItemToCart(item)}
            showSearch={false}
          />
        )}

        {/* Friggitoria */}
        {activeMenuTab === 'friggitoria' && (
          <MenuSection
            title="La Friggitoria"
            items={menuItemsLoading ? friggitoriaItems : menuFriggitoria}
            isItemAvailable={isItemAvailable}
            loadingItemId={loadingItemId}
            onAddToCart={addItemToCartDirectly}
            showSearch={true}
          />
        )}

        {/* Bevande */}
        {activeMenuTab === 'bevande' && (
          <>
            <MenuSection
              title="Bevande Non Alcoliche"
              items={(menuItemsLoading ? bevandeItems : menuBevande).filter((i) => i.category === 'non-alcoholic' || !i.category)}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onAddToCart={addItemToCartDirectly}
              showSearch={false}
              gridLayout={true}
            />
            <MenuSection
              title="Bevande Alcoliche"
              items={(menuItemsLoading ? bevandeItems : menuBevande).filter((i) => i.category === 'alcoholic')}
              isItemAvailable={isItemAvailable}
              loadingItemId={loadingItemId}
              onAddToCart={addItemToCartDirectly}
              showSearch={false}
              gridLayout={true}
            />
          </>
        )}

        {/* ── Carrello e form ordine ── */}
        <section ref={cartRef} className="bg-white p-6 rounded-lg shadow-md md:col-span-2">
          <h2 className="text-2xl font-bold mb-4 text-red-700">Il Tuo Ordine</h2>

          {cart.length === 0 ? (
            <p className="text-gray-600 italic">Il tuo carrello è vuoto.</p>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {cart.map((item) => (
                  <div key={item.cartItemId} className="flex justify-between items-start border-b pb-2">
                    <div className="flex-grow mr-2">
                      <span className="font-medium text-gray-900">{item.name}</span>
                      {item.description && !(item.type === 'pizza-due-gusti' || item.type === 'maxi-pizza-due-gusti') && (
                        <p className="text-gray-600 text-sm mt-0.5">{item.description}</p>
                      )}
                      {(item.type === 'pizza-due-gusti' || item.type === 'maxi-pizza-due-gusti') && (
                        <div className="text-gray-500 text-xs mt-1 space-y-0.5">
                          <p><span className="font-semibold">Metà 1 ({item.rawFirstHalf?.selectedPizza?.name}):</span>{' '}
                            {item.firstHalfMods?.length ? item.firstHalfMods.join(', ') : 'Nessuna modifica'}</p>
                          <p><span className="font-semibold">Metà 2 ({item.rawSecondHalf?.selectedPizza?.name}):</span>{' '}
                            {item.secondHalfMods?.length ? item.secondHalfMods.join(', ') : 'Nessuna modifica'}</p>
                        </div>
                      )}
                      {item.modifications?.length > 0 && !(item.type === 'pizza-due-gusti' || item.type === 'maxi-pizza-due-gusti') && (
                        <p className="text-gray-500 text-xs mt-0.5">Modifiche: {item.modifications.join(', ')}</p>
                      )}
                    </div>
                    <div className="flex items-center flex-shrink-0 gap-1">
                      <button onClick={() => decreaseQuantity(item.cartItemId)} className="bg-gray-200 text-gray-700 w-7 h-7 rounded-full hover:bg-gray-300 font-bold">-</button>
                      <span className="text-base font-bold text-gray-900 w-5 text-center">{item.quantity}</span>
                      <button onClick={() => increaseQuantity(item.cartItemId)} className="bg-gray-200 text-gray-700 w-7 h-7 rounded-full hover:bg-gray-300 font-bold">+</button>
                      <span className="font-semibold text-gray-800 ml-2 w-16 text-right">€{(item.price * item.quantity).toFixed(2)}</span>
                      <button onClick={() => removeFromCart(item.cartItemId)} className="text-red-500 hover:text-red-700 ml-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Riepilogo prezzi con riga consegna separata (UX FIX) */}
              <div className="text-sm text-gray-700 mb-4 space-y-1 border-t pt-3">
                <div className="flex justify-between">
                  <span>Subtotale</span>
                  <span>€{cart.reduce((s, i) => s + i.price * i.quantity, 0).toFixed(2)}</span>
                </div>
                {deliveryOption === 'delivery' && cart.length > 0 && (
                  <>
                    <div className={`flex justify-between ${deliveryFee === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                      <span>Costo consegna</span>
                      {deliveryFee === 0
                        ? <span className="font-semibold">🎉 Gratis</span>
                        : <span>+€{cartSubtotal < 10 ? '3.50' : deliveryFee.toFixed(2)}</span>
                      }
                    </div>
                    {cartSubtotal < 10 && (
                      <div className="flex justify-between text-orange-500 text-xs">
                        <span>Supplemento ordine inferiore a €10</span>
                        <span>+€1.00</span>
                      </div>
                    )}
                    {deliveryFee === 0 && (
                      <p className="text-green-600 text-xs">Spesa superiore a €100 – consegna gratuita!</p>
                    )}
                  </>
                )}
                {/* Messaggio upsell soglia consegna */}
                {deliveryOption === 'delivery' && (() => {
                  const friggitoriaLink = (
                    <button
                      onClick={() => { setActiveMenuTab('friggitoria'); cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="underline font-bold hover:text-orange-600 transition-colors"
                    >
                      frittini
                    </button>
                  );
                  const proTip = (
                    <p className="text-orange-500 text-xs mt-0.5">
                      🍟 Pro tip: accompagna la pizza con uno dei nostri {friggitoriaLink}!
                    </p>
                  );
                  if (cartSubtotal >= 95 && cartSubtotal < 100) {
                    const diff = (100 - cartSubtotal).toFixed(2);
                    return <>{<p className="text-green-600 text-xs mt-1 font-medium">🎉 Aggiungi solo €{diff} per avere la consegna <strong>gratuita</strong>!</p>}{proTip}</>;
                  }
                  if (cartSubtotal >= 65 && cartSubtotal < 70) {
                    const diff = (70 - cartSubtotal).toFixed(2);
                    return <>{<p className="text-blue-600 text-xs mt-1 font-medium">💡 Aggiungi solo €{diff} per ridurre la consegna a <strong>€2.00</strong>!</p>}{proTip}</>;
                  }
                  if (cartSubtotal >= 25 && cartSubtotal < 30) {
                    const diff = (30 - cartSubtotal).toFixed(2);
                    return <>{<p className="text-blue-600 text-xs mt-1 font-medium">💡 Aggiungi solo €{diff} per ridurre la consegna a <strong>€2.50</strong>!</p>}{proTip}</>;
                  }
                  if (cartSubtotal >= 5 && cartSubtotal < 10) {
                    const diff = (10 - cartSubtotal).toFixed(2);
                    return <>{<p className="text-blue-600 text-xs mt-1 font-medium">💡 Aggiungi solo €{diff} per ridurre la consegna a <strong>€3.50</strong>!</p>}{proTip}</>;
                  }
                  return null;
                })()}
                <div className="flex justify-between text-lg font-bold text-green-700 border-t pt-1 mt-1">
                  <span>Totale</span>
                  <span>€{calculateTotal()}</span>
                </div>
              </div>

              <button onClick={clearCart} className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors mb-4">Svuota Carrello</button>
            </>
          )}

          {/* ── Opzioni consegna ── */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-900">Opzioni di Consegna</h3>
            <div className="flex flex-col space-y-2">
              {[['pickup', 'Ritiro in negozio'], ['delivery', 'Consegna a domicilio']].map(([val, label]) => (
                <label key={val} className="inline-flex items-center">
                  <input type="radio" className="form-radio h-5 w-5 text-red-600" name="deliveryOption" value={val}
                    checked={deliveryOption === val}
                    onChange={() => { setDeliveryOption(val); setSelectedTime(''); setPickupName(''); setPickupPhone(''); }} />
                  <span className="ml-2 text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* ── Selezione data ── */}
          <div className="mb-4">
            <label htmlFor="order-date" className="block text-lg font-semibold mb-2 text-gray-900">Seleziona Data</label>
            <input type="date" id="order-date" value={selectedDate} min={getMinDate()}
              onChange={(e) => { setSelectedDate(e.target.value); setSelectedTime(''); }}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
            />
            {/* Avvisi giorno selezionato */}
            {dayStatusMessage && (
              <p className={`mt-2 text-sm font-semibold rounded-md px-3 py-2 border ${
                dayStatusMessage.color === 'red' ? 'text-red-700 bg-red-50 border-red-200' :
                dayStatusMessage.color === 'green' ? 'text-green-700 bg-green-50 border-green-200' :
                'text-orange-700 bg-orange-50 border-orange-200'
              }`}>
                {dayStatusMessage.text}
              </p>
            )}
            {deliveryNotice && (
              <p className="mt-1 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                {deliveryNotice}
              </p>
            )}
          </div>

          {/* ── Selezione orario ── */}
          <div className="mb-6">
            <label htmlFor="time-slot" className="block text-lg font-semibold mb-2 text-gray-900">Seleziona Orario</label>
            <select id="time-slot" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm">
              <option value="">Scegli un orario</option>
              {currentTimeslots.length === 0 && <option disabled>Nessuno slot disponibile per questa data.</option>}
              {currentTimeslots.map((time, i) =>
                time === '---'
                  ? <option key={i} disabled>───────────</option>
                  : <option key={time} value={time}>{time}</option>
              )}
            </select>
          </div>

          {/* ── Ritiro ── */}
          {deliveryOption === 'pickup' && (
            <>
              <div className="mb-4">
                <label htmlFor="pickup-name" className="block text-sm font-medium text-gray-700 mb-1">Nome per il ritiro</label>
                <input type="text" id="pickup-name" value={pickupName} onChange={(e) => setPickupName(e.target.value)} placeholder="Es: Mario Rossi"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
              </div>
              <div className="mb-6">
                <label htmlFor="pickup-phone" className="block text-sm font-medium text-gray-700 mb-1">Numero di telefono</label>
                <input type="tel" id="pickup-phone" value={pickupPhone} onChange={(e) => setPickupPhone(e.target.value)} placeholder="Es: 3331234567"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
              </div>
            </>
          )}

          {/* ── Consegna ── */}
          {deliveryOption === 'delivery' && (
            <div className="mb-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Dettagli Indirizzo</h3>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm shadow-sm">
                <div className="flex items-center mb-2"><span className="text-xl mr-2">🚚</span><h4 className="font-bold uppercase tracking-wide">Le nostre zone di consegna</h4></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><p className="font-bold text-amber-800 mb-1">📍 Comuni (interi)</p><p className="text-amber-700 italic">Casciago, Luvinate, Barasso, Comerio.</p></div>
                  <div><p className="font-bold text-amber-800 mb-1">🏘️ Varese Città</p><p className="text-amber-700"><span className="text-xs font-semibold uppercase">Solo quartieri:</span><br/>Avigno, Velate, Masnago, Sant&apos;Ambrogio, Montello, Brunella, Calcinate degli Origoni.</p></div>
                </div>
                <p className="mt-3 text-[11px] text-amber-600 border-t border-amber-200 pt-2 italic">* Se la tua zona non è in elenco, contattaci telefonicamente prima di ordinare.</p>
              </div>
              {[['street','Via/Piazza','text','Es: Via Roma 123'],['intercomName','Nome sul citofono','text','Es: Rossi'],['city','Città','text','Es: Varese'],['zip','CAP','text','Es: 21020'],['phone','Telefono','tel','Es: 3331234567']].map(([name, label, type, placeholder]) => (
                <div key={name}>
                  <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} id={name} name={name} value={address[name]} placeholder={placeholder}
                    onChange={(e) => setAddress((prev) => ({ ...prev, [name]: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
                </div>
              ))}
              <p className="text-sm text-blue-600 font-semibold italic p-2 bg-blue-50 rounded-md border border-blue-200">
                NB: Gli orari per la consegna a domicilio rappresentano una preferenza. Assegneremo un orario il più vicino possibile a quello richiesto.
              </p>
            </div>
          )}

          {/* ── Note ── */}
          <div className="mb-6">
            <label htmlFor="order-notes" className="block text-lg font-semibold mb-2 text-gray-900">Note per l&apos;ordine (facoltativo)</label>
            <textarea id="order-notes" rows="3" value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Es: Pizza ben cotta, consegna al secondo piano…"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
          </div>

          {/* ── Pulsante ordina ── */}
          <button
            onClick={() => {
              if (!isUserAuthenticated) { setShowAuthModal(true); setAuthMode('login'); setAuthError(''); setAuthEmail(''); setAuthPassword(''); setPrivacyPolicyAccepted(false); }
              else placeOrder();
            }}
            disabled={!isFirebaseReady}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-colors text-lg disabled:opacity-50"
          >
            {isFirebaseReady
              ? isUserAuthenticated ? 'Ordina Ora' : 'Accedi per ordinare'
              : 'Caricamento sistema…'}
          </button>
        </section>
      </main>

      {/* ── Modal messaggi generali ── */}
      {showModal && message && !showAuthModal && !cancelOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm text-center">
            <h3 className="text-xl font-bold mb-4 text-red-700">Notifica</h3>
            <p className="text-gray-700 mb-6">{message}</p>
            <button onClick={() => { setShowModal(false); setMessage(''); }} className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md shadow-sm">Chiudi</button>
          </div>
        </div>
      )}

      {/* ── Modal annullamento ordine ── */}
      {cancelOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm text-center">
            <h3 className="text-xl font-bold mb-4 text-red-700">Conferma Annullamento</h3>
            <p className="text-gray-700 mb-6">
              Vuoi annullare l&apos;ordine <strong>{cancelOrderModal.orderId}</strong>?<br/>
              Orario {cancelOrderModal.isDelivery ? 'consegna' : 'ritiro'}: <strong>{cancelOrderModal.orderTime}</strong> ({cancelOrderModal.orderDate})<br/>
              Mancano circa <strong>{cancelOrderModal.timeRemaining} minuti</strong>.
            </p>
            <div className="flex justify-center space-x-4">
              <button onClick={() => setCancelOrderModal(null)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-md">No, torna indietro</button>
              <button onClick={handleCancelOrder} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md">Sì, annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Storico ordini ── */}
      {showOrderHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl overflow-y-auto max-h-[90vh]">
            <h3 className="text-2xl font-bold mb-4 text-red-700 text-center">I Miei Ordini</h3>
            {userOrders.length === 0 ? (
              <p className="text-gray-600 text-center">Non hai ancora effettuato ordini.</p>
            ) : (
              <div className="space-y-4">
                {userOrders.map((order) => (
                  <div key={order.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-semibold text-gray-900">ID: <span className="text-red-600">{order.orderId}</span></p>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${order.status === 'pending' ? 'bg-yellow-200 text-yellow-800' : order.status === 'approved' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                        {order.status === 'pending' ? 'In attesa' : order.status === 'approved' ? 'Confermato' : order.status === 'cancelled' ? 'Annullato' : order.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">Data: {new Date(order.timestamp).toLocaleString('it-IT')}</p>
                    <p className="text-sm text-gray-700">Orario {order.deliveryOption === 'pickup' ? 'ritiro' : 'consegna'}: {order.confirmedTime || order.selectedTime} ({order.selectedDate})</p>
                    <p className="text-sm font-semibold text-gray-700">Totale: €{order.total?.toFixed(2)}</p>
                    {order.staffMessage && <p className="text-sm text-blue-600 mt-1">Staff: {order.staffMessage}</p>}
                    {(order.status === 'pending' || order.status === 'approved') && (
                      <button onClick={() => confirmCancelOrder(order.id, order.selectedTime, order.confirmedTime, order.deliveryOption, order.selectedDate)}
                        className="mt-3 bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md text-sm">
                        Annulla Ordine
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowOrderHistory(false)} className="mt-6 w-full bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md">Chiudi</button>
          </div>
        </div>
      )}

      {/* ── Modal personalizzazione pizza singola ── */}
      {showCustomizationModal && itemToCustomize && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md overflow-y-auto max-h-[90vh]">
            <h3 className="text-2xl font-bold mb-2 text-red-700">Personalizza {itemToCustomize.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{itemToCustomize.description}</p>
            <CustomizationSection
              itemBase={itemToCustomize}
              currentAdditions={customAdditions}
              currentRemovals={customRemovals}
              onAddAddition={(name, price) => { setCustomAdditions((p) => [...p, { name, price }]); setCustomCost((c) => c + price); }}
              onRemoveAddition={(i) => {
                setCustomAdditions((p) => { const n = p.filter((_, idx) => idx !== i); setCustomCost(n.reduce((s, m) => s + m.price, 0)); return n; });
              }}
              onAddRemoval={(name) => setCustomRemovals((p) => [...p, name])}
              onRemoveRemoval={(i) => setCustomRemovals((p) => p.filter((_, idx) => idx !== i))}
              availableAddableExtras={fsExtras}
              removableIngredients={fsRemovals}
            />
            <p className="text-base font-bold text-right text-gray-900 mt-2">
              Prezzo con modifiche: €{(itemToCustomize.price + customCost).toFixed(2)}
            </p>
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={closeCustomizationModal} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-md">Annulla</button>
              <button onClick={() => { addItemToCart(itemToCustomize, customAdditions, customRemovals); closeCustomizationModal(); }}
                className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md">Conferma e Aggiungi</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Due Gusti normale ── */}
      {showTwoFlavorModal && (
        <TwoFlavorModal
          isMaxi={false} step={twoFlavorStep} setStep={setTwoFlavorStep}
          firstHalf={firstHalfDetails} setFirstHalf={setFirstHalfDetails}
          secondHalf={secondHalfDetails} setSecondHalf={setSecondHalfDetails}
          pizzaList={menuItemsLoading ? [...pizzas, ...specialtyPizzas] : [...menuPizzas, ...menuSpecialtyPizzas]} isItemAvailable={isItemAvailable}
          onFinalize={() => finalizeTwoFlavorPizza(false)}
          onClose={() => { setShowTwoFlavorModal(false); setTwoFlavorStep(1); setFirstHalfDetails(emptyHalf()); setSecondHalfDetails(emptyHalf()); }}

        />
      )}

      {/* ── Modal Due Gusti Maxi ── */}
      {showMaxiTwoFlavorModal && (
        <TwoFlavorModal
          isMaxi={true} step={maxiTwoFlavorStep} setStep={setMaxiTwoFlavorStep}
          firstHalf={firstHalfMaxiDetails} setFirstHalf={setFirstHalfMaxiDetails}
          secondHalf={secondHalfMaxiDetails} setSecondHalf={setSecondHalfMaxiDetails}
          pizzaList={menuItemsLoading ? [...maxiPizzas, ...maxiSpecialtyPizzas] : [...menuMaxiPizzas, ...menuMaxiSpecialtyPizzas]} isItemAvailable={isItemAvailable}
          onFinalize={() => finalizeTwoFlavorPizza(true)}
          onClose={() => { setShowMaxiTwoFlavorModal(false); setMaxiTwoFlavorStep(1); setFirstHalfMaxiDetails(emptyHalf()); setSecondHalfMaxiDetails(emptyHalf()); }}

        />
      )}

      {/* ── Auth Modal ── */}
      {showAuthModal && (
        <AuthModal
          authMode={authMode} setAuthMode={setAuthMode}
          authEmail={authEmail} setAuthEmail={setAuthEmail}
          authPassword={authPassword} setAuthPassword={setAuthPassword}
          authError={authError}
          privacyPolicyAccepted={privacyPolicyAccepted} setPrivacyPolicyAccepted={setPrivacyPolicyAccepted}
          onSubmit={handleAuthSubmit}
          onSocialLogin={handleSocialLogin}
          onClose={() => setShowAuthModal(false)}
          onShowPrivacyPolicy={() => setShowPrivacyPolicyModal(true)}
        />
      )}

      {/* ── Privacy Policy Modal ── */}
      {showPrivacyPolicyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-red-700">Privacy Policy e Cookie Policy</h3>
              <button onClick={() => setShowPrivacyPolicyModal(false)} className="text-red-500 hover:text-red-700" aria-label="Chiudi">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <PrivacyPolicyContent />
            <button onClick={() => setShowPrivacyPolicyModal(false)} className="mt-6 w-full bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-md">Chiudi</button>
          </div>
        </div>
      )}

      {/* ── Cookie Consent Banner ── */}
      {showCookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 bg-opacity-95 text-white p-4 flex flex-col sm:flex-row items-center justify-between z-50 gap-4">
          <div className="flex-grow text-sm text-center sm:text-left">
            <p>Utilizziamo cookie per migliorare la tua esperienza.{' '}
              <button onClick={() => { setShowCookieConsent(false); setShowPrivacyPolicyModal(true); }} className="text-blue-300 hover:underline">Privacy & Cookie Policy</button>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={handleAcceptAllCookies} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm">Accetta Tutti</button>
            <button onClick={handleRejectAllCookies} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm">Rifiuta Tutti</button>
            <button onClick={() => { setShowCookieConsent(false); setShowCookieSettingsModal(true); }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm">Personalizza</button>
          </div>
        </div>
      )}

      {/* ── Cookie Settings Modal ── */}
      {showCookieSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-2xl font-bold mb-4 text-red-700 text-center">Impostazioni Cookie</h3>
            <p className="text-gray-700 mb-4 text-sm">I cookie funzionali sono sempre attivi. Puoi gestire gli altri qui sotto.</p>
            <div className="space-y-3">
              {[['functional','Cookie Funzionali',true],['analytics','Cookie di Analisi',false],['marketing','Cookie di Marketing',false]].map(([key, label, disabled]) => (
                <div key={key} className="flex items-center justify-between">
                  <label htmlFor={`${key}-cookies`} className="text-gray-900 font-medium">{label}</label>
                  <input type="checkbox" id={`${key}-cookies`} checked={cookiePreferences[key]} disabled={disabled}
                    onChange={(e) => setCookiePreferences((p) => ({ ...p, [key]: e.target.checked }))}
                    className="form-checkbox h-5 w-5 text-red-600 rounded-md" />
                </div>
              ))}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => { setShowCookieSettingsModal(false); setShowCookieConsent(true); }} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-5 py-2 rounded-md">Annulla</button>
              <button onClick={handleSaveCookiePreferences} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md">Salva</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success toast ── */}
      {showSuccessMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-500 text-white px-5 py-3 rounded-lg shadow-lg z-50 transition-opacity">
          Prodotto aggiunto al carrello! ✓
        </div>
      )}

      {/* ── Footer ── */}
      <footer ref={privacyPolicyRef} id="privacy-policy" className="w-full max-w-4xl mt-8 rounded-lg shadow-lg overflow-hidden">
        <div className="w-full bg-gray-900 text-gray-100 p-6">
          <p className="text-lg font-bold mb-4 text-center">© 2025 Pizzeria Cavour</p>
          <div className="flex flex-col gap-3 mb-5">
            <a href="https://www.google.com/maps/search/?api=1&query=Pizzeria+Cavour+Via+Matteotti+11+Casciago" target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 p-3 bg-gray-800 rounded-md border border-gray-700 hover:bg-gray-700 transition-colors">
              <span className="text-xl">📍</span><span className="text-blue-300 font-medium">Via Matteotti 11, Casciago (VA)</span>
            </a>
            <a href="tel:+390332212873" className="flex items-center justify-center gap-2 p-3 bg-gray-800 rounded-md border border-gray-700 hover:bg-gray-700 transition-colors">
              <span className="text-xl">📞</span><span className="text-blue-300 font-medium">+39 0332 212873</span>
            </a>
          </div>
          <div className="border-t border-gray-800 pt-4 text-center">
            <p className="text-sm">
              <button onClick={() => setShowPrivacyPolicyModal(true)} className="text-gray-400 underline">Privacy Policy</button>
              <span className="mx-2 text-gray-600">|</span>
              <button onClick={() => setShowPrivacyPolicyModal(true)} className="text-gray-400 underline">Cookie Policy</button>
            </p>
            <p className="text-xs mt-2 text-gray-500">P.IVA: 02865620120 – REA VA296713</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
